import { AtollonEvent } from "./AtollonEvent";

export interface ScriptBridgeHandlers {
  handleFormValueChanged: (event: AtollonEvent) => void;
  handleRequestData: (event: AtollonEvent) => void | Promise<void>;
  handleDisplayWarning: (event: AtollonEvent) => void;
  handleDisplaySuccess: (event: AtollonEvent) => void;
}

let activeCount = 0;
const handlersRegistry = new Set<ScriptBridgeHandlers>();
let windowListenersAttached = false;

function createFormValueChangedWrapper(): (evt: Event) => void {
  return (evt: Event) => {
    for (const h of handlersRegistry) {
      h.handleFormValueChanged(evt as AtollonEvent);
    }
  };
}

function createRequestDataWrapper(): (evt: Event) => void {
  return (evt: Event) => {
    const first = handlersRegistry.values().next().value;
    if (first) first.handleRequestData(evt as AtollonEvent);
  };
}

function createDisplayWarningWrapper(): (evt: Event) => void {
  return (evt: Event) => {
    const first = handlersRegistry.values().next().value;
    if (first) first.handleDisplayWarning(evt as AtollonEvent);
  };
}

function createDisplaySuccessWrapper(): (evt: Event) => void {
  return (evt: Event) => {
    const first = handlersRegistry.values().next().value;
    if (first) first.handleDisplaySuccess(evt as AtollonEvent);
  };
}

let boundFormValueChanged: (evt: Event) => void;
let boundRequestData: (evt: Event) => void;
let boundDisplayWarning: (evt: Event) => void;
let boundDisplaySuccess: (evt: Event) => void;

function attachWindowListeners(): void {
  if (windowListenersAttached) return;
  windowListenersAttached = true;

  boundFormValueChanged = createFormValueChangedWrapper();
  boundRequestData = createRequestDataWrapper();
  boundDisplayWarning = createDisplayWarningWrapper();
  boundDisplaySuccess = createDisplaySuccessWrapper();

  window.addEventListener(AtollonEvent.FORM_VALUE_CHANGED, boundFormValueChanged);
  window.addEventListener(AtollonEvent.REQUEST_DATA, boundRequestData);
  window.addEventListener(AtollonEvent.DISPLAY_WARNING, boundDisplayWarning);
  window.addEventListener(AtollonEvent.DISPLAY_SUCCESS, boundDisplaySuccess);
}

function detachWindowListeners(): void {
  if (!windowListenersAttached) return;
  windowListenersAttached = false;

  window.removeEventListener(
    AtollonEvent.FORM_VALUE_CHANGED,
    boundFormValueChanged
  );
  window.removeEventListener(AtollonEvent.REQUEST_DATA, boundRequestData);
  window.removeEventListener(AtollonEvent.DISPLAY_WARNING, boundDisplayWarning);
  window.removeEventListener(AtollonEvent.DISPLAY_SUCCESS, boundDisplaySuccess);
}

function cleanupScriptListeners(): void {
  const removeScriptListeners = (window as any).removeScriptListeners;
  if (typeof removeScriptListeners === "function") {
    try {
      removeScriptListeners();
    } catch (e) {
      console.warn(
        "removeScriptListeners threw an error (likely missing dependency such as jQuery):",
        e
      );
    }
  } else {
    console.warn("No removeScriptListeners function found");
  }
  (window as any).removeScriptListeners = null;
}

export function addFormInstance(handlers: ScriptBridgeHandlers): void {
  activeCount++;
  handlersRegistry.add(handlers);
  if (activeCount === 1) {
    attachWindowListeners();
  }
}

export function removeFormInstance(handlers: ScriptBridgeHandlers): void {
  handlersRegistry.delete(handlers);
  activeCount--;
  if (activeCount <= 0) {
    activeCount = 0;
    detachWindowListeners();
    cleanupScriptListeners();
  }
}
